import {FC, useState, useEffect} from 'react'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Card, Row, Col, Spinner, Button, Form} from 'react-bootstrap'
import {useParams, useSearchParams, useNavigate} from 'react-router-dom'
import {useAbility} from '@casl/react'
import {AbilityContext} from '../../../modules/auth/core/AbilityContext'
import NoPermissionCard from '../../../../_metronic/layout/components/permission/NoPermissionCard'
import {getTariffData, approveTariffData} from './core/_requests'
import {Toast} from '../../../../ui-kit'

const ReactSwal = withReactContent(Swal)
const confirmDialog = (title: string) => {
  return ReactSwal.fire({
    title,
    icon: 'question',
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    customClass: {
      title: 'fw-bold',
      cancelButton: 'btn btn-danger btn-sm',
      confirmButton: 'btn btn-success btn-sm',
    },
    padding: '1rem',
  })
}

type ColumnConfig = {
  header: string
  field: string
  type: 'text' | 'number' | 'select' | 'checkbox'
}

const TariffReviewPage: FC = () => {
  const {typeTariff} = useParams()
  const ability = useAbility(AbilityContext)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const created_at = searchParams.get('created_at')
  const created_by = searchParams.get('created_by')

  const canAccess =
    ability.can('approve', 'PERM_APPROVE_PREF_TARIFF') ||
    ability.can('approve', 'PERM_APPROVE_MFN_TARIFF') ||
    ability.can('approve', 'PERM_APPROVE_BOUND_TARIFF')

  const axiosPrivate = useAxiosPrivate()

  const [tariffTableData, setTariffTableData] = useState<any>([])
  const [tableLoading, setTableLoading] = useState(false)
  const [isLoadingButton, setIsLoadingButton] = useState(false)

  const fetchTariffData = async () => {
    if (!created_at && !created_by) return
    setTableLoading(true)
    try {
      const tariffParams = {
        created_at: created_at?.replace('+', ' ').replace('%3A', ':'),
        created_by: created_by,
      }
      const resTariffData = await getTariffData(axiosPrivate, tariffParams, typeTariff!)
      setTariffTableData(resTariffData.data.data || [])
    } catch (error) {
      console.error(error)
      Toast('Error when fetching tariff data', 'danger', '')
    } finally {
      setTableLoading(false)
    }
  }

  useEffect(() => {
    fetchTariffData()
  }, [created_at, typeTariff])

  const handleInputChange = (index: number, field: string, value: string | number | boolean) => {
    const updatedList = [...tariffTableData]
    updatedList[index] = {...updatedList[index], [field]: value}
    setTariffTableData(updatedList)
  }

  const handleCancel = () => {
    confirmDialog('Are you sure you want to cancel?').then((result) => {
      if (result.isConfirmed) {
        navigate('/home')
      }
    })
  }

  const handleSaveAndApprove = async () => {
    confirmDialog('Are you sure you want to approve this tariff data?').then((result) => {
      if (result.isConfirmed) {
        setIsLoadingButton(true)
        approveTariffData(axiosPrivate, {
          type: typeTariff as 'bound' | 'mfn' | 'pref', // ensure type safety if using shared request
          created_at: created_at!,
          created_by: created_by!,
          tariffTableData,
        })
          .then(() => {
            ReactSwal.fire('Success', 'Tariff data approved!', 'success').then(() => {
              navigate('/home') // Redirect back to dashboard
            })
          })
          .catch((err) => {
            console.error(err)
            ReactSwal.fire('Error', 'Approval failed', 'error')
          })
          .finally(() => {
            setIsLoadingButton(false)
          })
      }
    })
  }

  const renderEditableTable = () => {
    if (tableLoading) return <div>Loading...</div>
    let columns: ColumnConfig[] = []

    switch (typeTariff) {
      case 'bound':
        columns = [
          {header: 'HS Code', field: 'hs', type: 'text'},
          {header: 'HS Version', field: 'hs_version', type: 'select'},
          {header: 'HS Rev', field: 'hs_rev', type: 'text'},
          {header: 'Ave Avg.', field: 'ave_avg', type: 'number'},
        ]
        break
      case 'mfn':
        columns = [
          {header: 'HS Code', field: 'hs', type: 'text'},
          {header: 'Year', field: 'year', type: 'number'},
          {header: 'Val', field: 'val', type: 'number'},
          {header: 'Year Org', field: 'year_org', type: 'text'},
          {header: 'HS Ver Org', field: 'hs_ver_org', type: 'select'},
          {header: 'Is Avg.?', field: 'isAVE', type: 'checkbox'},
          {header: 'Source', field: 'src', type: 'text'},
        ]
        break
      case 'pref':
        break
    }

    return (
      <div className='table-responsive'>
        <table className='table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3'>
          <thead>
            <tr className='fw-bold text-muted'>
              {columns.map((column, idx) => (
                <th key={idx}>{column.header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tariffTableData.map((row: any, tariffIndex: number) => (
              <tr key={tariffIndex}>
                {columns.map((col, colIdx) => {
                  if (col.type === 'select')
                    return (
                      <td key={colIdx}>
                        <Form.Select
                          onChange={(e) =>
                            handleInputChange(tariffIndex, col.field, e.target.value)
                          }
                          value={row.hs_ver_org}
                        >
                          <option value=''></option>
                          <option
                            value='H4'
                            selected={row.hs_ver_org === 'H4' || row.hs_version === 'H4'}
                          >
                            H4
                          </option>
                          <option
                            value='H5'
                            selected={row.hs_ver_org === 'H5' || row.hs_version === 'H5'}
                          >
                            H5
                          </option>
                          <option
                            value='H6'
                            selected={row.hs_ver_org === 'H6' || row.hs_version === 'H6'}
                          >
                            H6
                          </option>
                        </Form.Select>
                      </td>
                    )
                  if (col.type === 'checkbox')
                    return (
                      <td key={colIdx}>
                        <Form.Check
                          type='checkbox'
                          value={row.isAVE}
                          checked={row.isAVE}
                          onChange={() => handleInputChange(tariffIndex, col.field, !row.isAVE)}
                        />
                      </td>
                    )
                  return (
                    <td key={colIdx}>
                      <Form.Control
                        type={col.type}
                        className='form-control form-control-sm'
                        value={row[col.field] ?? ''}
                        onChange={(e) => handleInputChange(tariffIndex, col.field, e.target.value)}
                      />
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  let pageTitle = 'Tariff'
  let pageDesc = ''
  if (typeTariff === 'bound') {
    pageTitle = 'Review Bound Tariff'
    pageDesc = 'Bound'
  }
  if (typeTariff === 'mfn') {
    pageTitle = 'Review MFN Tariff'
    pageDesc = 'MFN'
  }
  if (typeTariff === 'pref') {
    pageTitle = 'Review Preferential Tariff'
    pageDesc = 'Preferential'
  }

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Home',
            path: '/',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
        ]}
      >
        {pageTitle}
      </PageTitle>
      {canAccess ? (
        <>
          <Row className='mb-5'>
            <Col>
              <Card className='card-custom card-border'>
                <Card.Header>
                  <Card.Title className='d-flex flex-column'>
                    <h3>{pageTitle}</h3>
                    <small className='text-gray-700 mt-2'>
                      {`These are ${pageDesc} tariffs that require review or approval:`}
                    </small>
                  </Card.Title>
                </Card.Header>
                <Card.Body>
                  {renderEditableTable()}
                  <div className='col-lg-12 d-flex justify-content-end gap-3 pt-5'>
                    <Button variant='default' className='btn-cancel' onClick={handleCancel}>
                      Cancel
                    </Button>
                    <Button
                      variant='primary'
                      onClick={handleSaveAndApprove}
                      disabled={tableLoading || tariffTableData.length === 0}
                    >
                      {isLoadingButton ? (
                        <>
                          <Spinner animation='border' size='sm' className='me-2' />
                          Submit
                        </>
                      ) : (
                        'Submit'
                      )}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      ) : (
        <NoPermissionCard />
      )}
    </>
  )
}

export default TariffReviewPage
